import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Flex,
  Tooltip,
  SimpleGrid,
  Divider
} from '@chakra-ui/react';
import {
  MdTrendingUp,
  MdAttachMoney,
  MdCheckCircle,
  MdSchedule,
  MdAssignment
} from 'react-icons/md';

const ComercialUserCard = ({ user, onClick }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const nameColor = useColorModeValue('gray.800', 'white');

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}K`;
    }
    return `R$ ${value.toFixed(0)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'Sem atividade';
    const diff = Date.now() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d atrás`;
    if (hours > 0) return `${hours}h atrás`;
    return 'Recente';
  };

  const getStatusColor = (status) => {
    const colors = {
      'novo': 'blue',
      'solicitacao_proposta': 'purple',
      'proposta_aceita': 'green',
      'proposta_negada': 'red',
      'solicitacao_documentos': 'orange',
      'documentos_completos': 'yellow',
      'em_analise': 'cyan',
      'fechado': 'green'
    };
    return colors[status] || 'gray';
  };

  const getPerformanceLevel = (successRate) => {
    if (successRate >= 70) return { label: 'Excelente', color: 'green' };
    if (successRate >= 50) return { label: 'Bom', color: 'blue' };
    if (successRate >= 30) return { label: 'Regular', color: 'yellow' };
    return { label: 'Precisa Melhorar', color: 'red' };
  };

  const performance = getPerformanceLevel(user.metrics.successRate);

  return (
    <Box
      bg={bgColor}
      p={5}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        bg: hoverBg,
        transform: 'translateY(-2px)',
        shadow: 'md'
      }}
      onClick={onClick}
    >
      <VStack align="stretch" spacing={4}>
        {/* Header */}
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Avatar size="md" name={user.name} />
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="md" color={nameColor}>
                {user.name}
              </Text>
              <Text fontSize="sm" color={textColor} noOfLines={1}>
                {user.email}
              </Text>
            </VStack>
          </HStack>
          <Badge colorScheme={performance.color} fontSize="xs">
            {performance.label}
          </Badge>
        </HStack>

        <Divider />

        {/* Metrics */}
        <SimpleGrid columns={2} spacing={3}>
          <Tooltip label="Total de Precatórios">
            <HStack>
              <Icon as={MdAssignment} color="blue.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold">
                  {user.metrics.totalPrecatorios}
                </Text>
                <Text fontSize="xs" color={textColor}>Precatórios</Text>
              </VStack>
            </HStack>
          </Tooltip>

          <Tooltip label="Valor Total Gerenciado">
            <HStack>
              <Icon as={MdAttachMoney} color="green.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold">
                  {formatCurrency(user.metrics.totalValue)}
                </Text>
                <Text fontSize="xs" color={textColor}>Valor Total</Text>
              </VStack>
            </HStack>
          </Tooltip>
        </SimpleGrid>

        {/* Success Metrics */}
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <HStack spacing={1}>
              <Icon as={MdCheckCircle} color="green.500" boxSize={4} />
              <Text fontSize="sm" color={textColor}>Taxa de Sucesso</Text>
            </HStack>
            <Text fontSize="sm" fontWeight="bold">
              {user.metrics.successRate}%
            </Text>
          </HStack>
          <Progress 
            value={user.metrics.successRate} 
            size="sm" 
            colorScheme="green"
            borderRadius="full"
          />
        </VStack>

        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <HStack spacing={1}>
              <Icon as={MdTrendingUp} color="blue.500" boxSize={4} />
              <Text fontSize="sm" color={textColor}>Taxa de Aceitação</Text>
            </HStack>
            <Text fontSize="sm" fontWeight="bold">
              {user.metrics.acceptanceRate}%
            </Text>
          </HStack>
          <Progress 
            value={user.metrics.acceptanceRate} 
            size="sm" 
            colorScheme="blue"
            borderRadius="full"
          />
        </VStack>

        {/* Status Distribution */}
        <Box>
          <Text fontSize="xs" color={textColor} mb={2}>Distribuição por Status:</Text>
          <Flex wrap="wrap" gap={1}>
            {Object.entries(user.metrics.statusDistribution).map(([status, count]) => (
              <Badge
                key={status}
                colorScheme={getStatusColor(status)}
                fontSize="xs"
                px={2}
              >
                {count}
              </Badge>
            ))}
          </Flex>
        </Box>

        <Divider />

        {/* Last Activity */}
        <HStack justify="space-between">
          <HStack spacing={1}>
            <Icon as={MdSchedule} color="gray.500" boxSize={4} />
            <Text fontSize="xs" color={textColor}>Última atividade</Text>
          </HStack>
          <Text fontSize="xs" fontWeight="medium">
            {formatDate(user.metrics.lastActivity)}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ComercialUserCard;